import { Box, Button, Chip, IconButton, MenuItem, Stack, TextField, Tooltip } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import GroupIcon from "@mui/icons-material/Group";
import PublishIcon from "@mui/icons-material/Publish";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { PagedTable, type Column, type SortState } from "../../components/PagedTable";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { SurveyDetailsDialog } from "./SurveyDetailsDialog";
import {
  DEFAULT_SURVEY_QUERY,
  downloadSurveyResponses,
  useCancelSurvey,
  useCopySurvey,
  useDeleteSurvey,
  usePublishSurvey,
  useRemindSurvey,
  useSurveys,
  type Survey,
  type SurveyQuery,
} from "./api";
import { Authority } from "../../auth/authorities";
import { useAuth } from "../../auth/useAuth";
import { useDebounced } from "../../hooks/useDebounced";
import { useNotify } from "../../app/useNotify";
import { errorMessageFor } from "../../api/client";
import { formatDateTimeWithZone } from "../../lib/format";
import { EmptyState } from "../../components/EmptyState";
import { surveyStatusColour, surveyStatusLabel } from "../../lib/labels";
import { usePageTitle } from "../../app/usePageTitle";

const STATUSES = ["PENDING", "PUBLISHED", "COMPLETED", "CANCELLED"];

type Pending = { survey: Survey; action: "publish" | "cancel" | "remind" | "copy" | "delete" };

export function SurveyListPage() {
  usePageTitle("Surveys");
  const { can } = useAuth();
  const notify = useNotify();
  const navigate = useNavigate();

  const [query, setQuery] = useState<SurveyQuery>(DEFAULT_SURVEY_QUERY);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Survey | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);

  useDebounced(search, 400, (term) =>
    setQuery((previous) => ({ ...previous, search: term, pageNumber: 0 })),
  );

  const filtered = query.search !== "" || Boolean(query.status);
  const surveys = useSurveys(query);
  const publish = usePublishSurvey();
  const cancel = useCancelSurvey();
  const remind = useRemindSurvey();
  const copy = useCopySurvey();
  const remove = useDeleteSurvey();

  const actions: Record<
    Pending["action"],
    {
      title: string;
      message: string;
      label: string;
      run: (id: number) => Promise<unknown>;
      success: string;
      destructive?: boolean;
    }
  > = {
    publish: {
      title: `Publish "${name}"?`,
      message:
        "Every app member will be able to answer it, and you will not be able to change its questions afterwards.",
      label: "Publish",
      run: (id) => publish.mutateAsync(id),
      success: "Survey published",
    },
    cancel: {
      title: `Cancel "${name}"?`,
      message: "It stops accepting answers. Responses already given are kept.",
      label: "Cancel survey",
      run: (id) => cancel.mutateAsync(id),
      success: "Survey cancelled",
      destructive: true,
    },
    remind: {
      title: `Remind members about "${name}"?`,
      message: "Everyone who has not answered yet gets a notification.",
      label: "Send reminder",
      run: (id) => remind.mutateAsync(id),
      success: "Reminder sent",
    },
    copy: {
      title: `Make a copy of "${name}"?`,
      message: "The copy is a draft with the same questions, and is not sent to anyone.",
      label: "Make a copy",
      run: (id) => copy.mutateAsync(id),
      success: "Survey copied",
    },
    delete: {
      title: `Delete "${name}"?`,
      message: "The survey and every answer given to it are removed. This cannot be undone.",
      label: "Delete",
      run: (id) => remove.mutateAsync(id),
      success: "Survey deleted",
      destructive: true,
    },
  };

  async function download(survey: Survey) {
    try {
      const blob = await downloadSurveyResponses(survey.id);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${survey.surveyName}.xlsx`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      notify.success("Responses downloaded");
    } catch (error) {
      notify.error(errorMessageFor(error) ?? "Could not download the responses.");
    }
  }

  const columns = useMemo<Column<Survey>[]>(
    () => [
      { key: "surveyName", label: "Survey", sortable: true },
      { key: "createdBy", label: "Created by" },
      {
        key: "status",
        label: "Status",
        sortable: true,
        render: (row) => (
          <Chip
            size="small"
            label={surveyStatusLabel(row.status)}
            color={surveyStatusColour(row.status)}
          />
        ),
      },
      { key: "startDate", label: "Start", render: (row) => formatDateTimeWithZone(row.startDate) },
      { key: "endDate", label: "End", render: (row) => formatDateTimeWithZone(row.endDate) },
      {
        key: "createdAt",
        label: "Created",
        sortable: true,
        render: (row) => formatDateTimeWithZone(row.createdAt),
      },
      {
        key: "actions",
        label: "Actions",
        align: "right",
        pinned: true,
        render: (row) => (
          <Stack direction="row" sx={{ justifyContent: "flex-end" }}>
            {can(Authority.EditSurvey) &&
              !["PUBLISHED", "COMPLETED", "CANCELLED"].includes(row.status) && (
                <Tooltip title="Edit">
                  <IconButton
                    size="small"
                    aria-label={`Edit ${row.surveyName}`}
                    onClick={() => {
                      setEditing(row);
                      setDialogOpen(true);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            {can(Authority.EditSurvey) && row.status === "PENDING" && (
              <Tooltip title="Targeting">
                <IconButton
                  size="small"
                  aria-label={`Targeting for ${row.surveyName}`}
                  onClick={() => navigate(`/dashboard/survey/${row.id}/targeting`)}
                >
                  <GroupIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {can(Authority.PublishSurvey) && row.status === "PENDING" && (
              <Tooltip title="Publish">
                <IconButton
                  size="small"
                  aria-label={`Publish ${row.surveyName}`}
                  onClick={() => setPending({ survey: row, action: "publish" })}
                >
                  <PublishIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {can(Authority.PublishSurvey) && row.status === "PUBLISHED" && (
              <>
                <Tooltip title="Send reminder">
                  <IconButton
                    size="small"
                    aria-label={`Remind ${row.surveyName}`}
                    onClick={() => setPending({ survey: row, action: "remind" })}
                  >
                    <NotificationsActiveIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cancel survey">
                  <IconButton
                    size="small"
                    aria-label={`Cancel ${row.surveyName}`}
                    onClick={() => setPending({ survey: row, action: "cancel" })}
                  >
                    <StopCircleIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {can(Authority.CreateSurvey) && row.status === "PUBLISHED" && (
              <Tooltip title="Copy">
                <IconButton
                  size="small"
                  aria-label={`Copy ${row.surveyName}`}
                  onClick={() => setPending({ survey: row, action: "copy" })}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {can(Authority.DownloadSurvey) && (
              <Tooltip title="Download responses">
                <IconButton
                  size="small"
                  aria-label={`Download ${row.surveyName}`}
                  onClick={() => download(row)}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {can(Authority.DeleteSurvey) && !["PUBLISHED", "COMPLETED"].includes(row.status) && (
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  aria-label={`Delete ${row.surveyName}`}
                  onClick={() => setPending({ survey: row, action: "delete" })}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [can],
  );

  const active = pending ? actions[pending.action] : null;

  return (
    <Box>
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", alignItems: "center", mb: 2, gap: 2 }}
      >
        {can(Authority.CreateSurvey) && (
          <Button
            variant="contained"
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
          >
            New survey
          </Button>
        )}
      </Stack>

      <Stack direction="row" sx={{ gap: 2, mb: 2, flexWrap: "wrap" }}>
        <TextField
          label="Search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ minWidth: 240 }}
        />
        <TextField
          select
          label="Status"
          value={query.status ?? ""}
          onChange={(event) =>
            setQuery((previous) => ({ ...previous, status: event.target.value, pageNumber: 0 }))
          }
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All</MenuItem>
          {STATUSES.map((status) => (
            <MenuItem key={status} value={status}>
              {surveyStatusLabel(status)}
            </MenuItem>
          ))}
        </TextField>
        <Button
          onClick={() => {
            setSearch("");
            setQuery(DEFAULT_SURVEY_QUERY);
          }}
        >
          Reset
        </Button>
      </Stack>

      <PagedTable
        columns={columns}
        rows={surveys.data?.data ?? []}
        rowKey={(row) => row.id}
        total={surveys.data?.total ?? 0}
        page={query.pageNumber}
        pageSize={query.pageSize}
        sort={{ sortOn: query.sortOn, sort: query.sort }}
        loading={surveys.isFetching}
        empty={
          <EmptyState
            filtered={filtered}
            noun="surveys"
            action={
              can(Authority.CreateSurvey)
                ? {
                    label: "Create your first survey",
                    onClick: () => {
                      setEditing(null);
                      setDialogOpen(true);
                    },
                  }
                : undefined
            }
            onClearFilters={() => {
              setSearch("");
              setQuery(DEFAULT_SURVEY_QUERY);
            }}
          />
        }
        onPageChange={(pageNumber) => setQuery((previous) => ({ ...previous, pageNumber }))}
        onPageSizeChange={(pageSize) =>
          setQuery((previous) => ({ ...previous, pageSize, pageNumber: 0 }))
        }
        onSortChange={(next: SortState) =>
          setQuery((previous) => ({ ...previous, ...next, pageNumber: 0 }))
        }
      />

      {dialogOpen && (
        <SurveyDetailsDialog
          key={editing?.id ?? "new"}
          open={dialogOpen}
          existing={editing}
          onClose={() => setDialogOpen(false)}
          onCreated={(survey) => navigate(`/dashboard/survey/${survey.id}/questions`)}
        />
      )}

      <ConfirmDialog
        open={pending !== null}
        title={active?.title ?? ""}
        message={active?.message}
        confirmLabel={active?.label ?? "Confirm"}
        destructive={active?.destructive}
        busy={
          publish.isPending ||
          cancel.isPending ||
          remind.isPending ||
          copy.isPending ||
          remove.isPending
        }
        onClose={() => setPending(null)}
        onConfirm={async () => {
          if (!pending || !active) return;
          try {
            await active.run(pending.survey.id);
            notify.success(active.success);
          } catch (error) {
            notify.error(errorMessageFor(error) ?? "That did not work. Nothing was changed.");
          } finally {
            setPending(null);
          }
        }}
      />
    </Box>
  );
}
